import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Coins, AlertCircle, CheckCircle2, Clock, Cake, DownloadCloud, UploadCloud } from "lucide-react";
import { Link } from "react-router-dom";
import Icon3D from "../components/Icon3D";

function formatMoney(v, ccy) {
  return `${Number(v || 0).toFixed(2)} ${ccy || "RON"}`;
}

function monthLabel(ym, locale) {
  if (!ym) return "";
  try {
    const [y, m] = ym.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    const LOCALES = { ro: "ro-RO", en: "en-GB", fr: "fr-FR", de: "de-DE", it: "it-IT", es: "es-ES", pt: "pt-PT", "pt-BR": "pt-BR", sr: "sr-Latn-RS" };
    return d.toLocaleDateString(LOCALES[locale] || LOCALES[String(locale || "").split("-")[0]] || "ro-RO", { month: "long", year: "numeric" });
  } catch { return ym; }
}

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [activity, setActivity] = useState([]);
  const [lodge, setLodge] = useState(null);
  const [anniv, setAnniv] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, e, a, l] = await Promise.all([
          api.get("/stats"),
          api.get("/events"),
          api.get("/activity?limit=6"),
          api.get("/lodge"),
        ]);
        setStats(s.data);
        const now = new Date().toISOString();
        setEvents(e.data.filter((ev) => ev.date >= now).slice(0, 4));
        setActivity(a.data);
        setLodge(l.data);
      } catch (err) {
        setError(formatApiError(err));
      }
      // Admin-only: this month's anniversaries (403 for regular users — ignore).
      api.get("/anniversaries").then(({ data }) => setAnniv(data)).catch(() => {});
    })();
  }, []);

  const shiftAnnivMonth = async (delta) => {
    if (!anniv) return;
    let m = anniv.month + delta;
    let y = anniv.year;
    if (m > 12) { m = 1; y += 1; }
    if (m < 1) { m = 12; y -= 1; }
    try {
      const { data } = await api.get(`/anniversaries?month=${m}&year=${y}`);
      setAnniv(data);
    } catch { /* ignore */ }
  };

  const { user } = useAuth();
  const restoreRef = useRef(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupProgress, setBackupProgress] = useState("");
  const [restoreBusy, setRestoreBusy] = useState(false);

  const doBackup = async () => {
    setBackupBusy(true);
    try {
      const { data: start } = await api.post("/backup/start");
      const jobId = start.job_id;
      let st = null;
      let pollFails = 0;
      // Poll the background job — big lodges can take several minutes.
      // Transient poll failures (backend restart / network blip) are
      // tolerated up to ~1 minute instead of aborting the whole backup.
      for (let i = 0; i < 900; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data } = await api.get(`/backup/status/${jobId}`);
          st = data;
          pollFails = 0;
        } catch (pe) {
          pollFails += 1;
          if (pollFails >= 30) throw pe;
          continue;
        }
        if (st.status !== "running") break;
        setBackupProgress(st.files_total ? `${st.files_done}/${st.files_total}` : "...");
      }
      if (!st || !["done", "partial"].includes(st.status)) throw new Error(st?.error || "Backup failed");
      // Native browser download (cookie auth) — the ZIP can be hundreds of
      // MB in production; buffering it through axios as a blob died with a
      // network error. The browser download manager streams + shows progress.
      const a = document.createElement("a");
      a.href = `${API}/backup/download/${jobId}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      if (st.status === "partial") {
        toast.warning(t(
          "dashboard.backupPartial",
          "Backup parțial: {{count}} fișiere nu au putut fi incluse. Descarcă arhiva, apoi reia backupul.",
          { count: st.files_failed || 0 },
        ));
      } else {
        toast.success(t("dashboard.backupOk", "Backup pornit la descărcare — păstrează fișierul într-un loc sigur."));
      }
    } catch (e) { toast.error(formatApiError(e)); } finally { setBackupBusy(false); setBackupProgress(""); }
  };

  const doRestore = async (ev) => {
    const f = ev.target.files?.[0];
    ev.target.value = "";
    if (!f) return;
    if (!window.confirm(t("dashboard.restoreConfirm", "ATENȚIE: restaurarea ÎNLOCUIEȘTE datele actuale cu cele din backup. Continui?"))) return;
    setRestoreBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/backup/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("dashboard.restoreOk", "Restaurare completă: {{docs}} documente în {{colls}} colecții.", { docs: data.documents, colls: data.collections }));
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) { toast.error(formatApiError(e)); } finally { setRestoreBusy(false); }
  };

  const tiles = stats
    ? [
        { label: t("dashboard.totalMembers"), value: stats.total_members, icon3d: "members" },
        { label: t("dashboard.upcomingEvents"), value: stats.upcoming_events, icon3d: "events" },
        { label: t("dashboard.newDocuments"), value: stats.total_documents, icon3d: "documents" },
        { label: t("dashboard.recognizedLodges"), value: stats.recognized_lodges, icon3d: "recognizedLodges" },
      ]
    : [];

  const membership = stats
    ? [
        { label: t("dashboard.activeMembers"), value: stats.active_members, icon3d: "person" },
        { label: t("dashboard.masterMasons"), value: stats.maestri, icon3d: "crown" },
        { label: t("dashboard.fellowCrafts"), value: stats.calfe, icon3d: "hammer" },
        { label: t("dashboard.enteredApprentices"), value: stats.ucenici, icon3d: "graduationCap" },
        { label: t("dashboard.honoraryMembers"), value: stats.honorary_members, icon3d: "star" },
      ]
    : [];

  // Officer list mirrors the canonical structure from the Lodge Info
  // page so both screens render the same names. Auto-detects obedience
  // context the same way `LodgeInfoPage` does — any populated `mare_*`
  // field flips the dashboard into the 11-step Grand-Lodge hierarchy
  // even when the legacy `is_obedience` flag wasn't set on the row.
  const hasAnyGrandOfficer = lodge && [
    "mare_maestru",
    "mare_prim_supraveghetor",
    "mare_al_doilea_supraveghetor",
    "mare_secretar",
    "mare_trezorier",
    "mare_cancelar",
    "mare_orator",
    "mare_expert",
    "mare_dir_ceremonii",
    "mare_filantrop",
    "mare_acoperitor",
  ].some((k) => (lodge[k] || "").toString().trim());
  const isObedience = !!lodge?.is_obedience || hasAnyGrandOfficer;
  const OFFICER_FIELDS = isObedience
    ? [
        // Fall back to the legacy lodge-level fields (`venerabil_maestru`,
        // `prim_supraveghetor`, etc.) when the matching Grand-Lodge field
        // hasn't been migrated yet. Lets data that was previously typed
        // into the obedience profile remain visible without forcing the
        // operator to retype it.
        { key: "mare_maestru", fallback: "venerabil_maestru", label: t("lodgeInfo.officerGM") },
        { key: "mare_prim_supraveghetor", fallback: "prim_supraveghetor", label: t("lodgeInfo.officerGSW") },
        { key: "mare_al_doilea_supraveghetor", fallback: "al_doilea_supraveghetor", label: t("lodgeInfo.officerGJW") },
        { key: "mare_secretar", fallback: "secretar", label: t("lodgeInfo.officerGSec") },
        { key: "mare_trezorier", fallback: "trezorierul", label: t("lodgeInfo.officerGTres") },
        { key: "mare_orator", label: t("lodgeInfo.officerGOrator") },
        { key: "mare_cancelar", label: t("lodgeInfo.officerGChan") },
        { key: "mare_expert", label: t("lodgeInfo.officerGExpert") },
        { key: "mare_dir_ceremonii", label: t("lodgeInfo.officerGDirCer") },
        { key: "mare_filantrop", label: t("lodgeInfo.officerGAlm") },
        { key: "mare_acoperitor", label: t("lodgeInfo.officerGInnerG") },
      ]
    : [
        { key: "venerabil_maestru", label: t("lodgeInfo.officerWM") },
        { key: "prim_supraveghetor", label: t("lodgeInfo.officerSW") },
        { key: "al_doilea_supraveghetor", label: t("lodgeInfo.officerJW") },
        { key: "secretar", label: t("lodgeInfo.officerSec") },
        { key: "trezorierul", label: t("lodgeInfo.officerTres") },
      ];

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title">{t("dashboard.title")}</h1>
          <p className="page-subtitle">{t("dashboard.subtitle")}</p>
        </div>
        {(user?.role === "admin") && (
          <div className="flex gap-2">
            <button className="btn-outline-gold flex items-center gap-1" onClick={doBackup} disabled={backupBusy} data-testid="dashboard-backup-btn">
              <DownloadCloud size={13} /> {backupBusy ? (backupProgress || "...") : t("dashboard.backup", "Backup")}
            </button>
            <button className="btn-outline-gold flex items-center gap-1" onClick={() => restoreRef.current?.click()} disabled={restoreBusy} data-testid="dashboard-restore-btn">
              <UploadCloud size={13} /> {restoreBusy ? "..." : t("dashboard.restore", "Restore")}
            </button>
            <input ref={restoreRef} type="file" accept=".zip,.json,application/zip,application/json" style={{ display: "none" }} onChange={doRestore} data-testid="dashboard-restore-input" />
          </div>
        )}
      </div>

      <div className="gold-divider my-8" />

      {error && <div className="error-banner mb-6">{error}</div>}

      {/* 4 main tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-tiles">
        {tiles.map((tile) => {
          const inner = (
            <div className="flex items-start justify-between">
              <div>
                <div className="tile-label">{tile.label}</div>
                <div className="tile-value" style={{ color: tile.warn ? "#9a7530" : undefined }}>{tile.value}</div>
              </div>
              <Icon3D name={tile.icon3d} size={56} alt={tile.label} />
            </div>
          );
          return tile.to ? (
            <Link key={tile.label} to={tile.to} className="tile" style={{ textDecoration: "none", color: "inherit" }}>{inner}</Link>
          ) : (
            <div key={tile.label} className="tile">{inner}</div>
          );
        })}
      </div>

      {/* Dues Widget — current month summary */}
      {stats?.dues_current_month && (
        <div className="mt-10 panel" data-testid="dues-widget">
          <div className="panel-head flex items-center" style={{ justifyContent: "space-between" }}>
            <span className="flex items-center gap-2"><Coins size={14} color="#e8c97a" /> {t("dashboard.duesMonth", { month: monthLabel(stats.dues_current_month.label, i18n.language).toUpperCase() })}</span>
            <Link to="/dues" style={{ color: "#e8c97a", fontSize: "0.7rem", textDecoration: "none", letterSpacing: "0.1em" }}>
              {t("dashboard.viewDetails")} →
            </Link>
          </div>
          <div className="panel-body">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link to="/dues" style={{ textDecoration: "none", color: "inherit" }} className="tile" data-testid="dues-tile-collected" >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="tile-label">{t("dashboard.collectedThisMonth")}</div>
                    <div className="tile-value" style={{ fontSize: "1.6rem", color: "#3e6a3c" }}>
                      {formatMoney(stats.dues_current_month.collected, stats.dues_current_month.currency)}
                    </div>
                    <div className="kv-label" style={{ fontSize: "0.62rem", marginTop: "0.25rem" }}>
                      {t("dashboard.invoicesPaid", { count: stats.dues_current_month.collected_count })}
                    </div>
                  </div>
                  <CheckCircle2 size={26} strokeWidth={1.3} color="#3e6a3c" />
                </div>
              </Link>
              <Link to="/dues" style={{ textDecoration: "none", color: "inherit" }} className="tile" data-testid="dues-tile-pending">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="tile-label">{t("dashboard.toCollect")}</div>
                    <div className="tile-value" style={{ fontSize: "1.6rem", color: "#9a7530" }}>
                      {formatMoney(stats.dues_current_month.pending, stats.dues_current_month.currency)}
                    </div>
                    <div className="kv-label" style={{ fontSize: "0.62rem", marginTop: "0.25rem" }}>
                      {t("dashboard.pendingCount", { count: stats.dues_current_month.pending_count })}
                    </div>
                  </div>
                  <Clock size={26} strokeWidth={1.3} color="#9a7530" />
                </div>
              </Link>
              <Link to="/dues" style={{ textDecoration: "none", color: "inherit" }} className="tile" data-testid="dues-tile-overdue">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="tile-label">{t("dashboard.overdueAll")}</div>
                    <div className="tile-value" style={{ fontSize: "1.6rem", color: stats.dues_overdue > 0 ? "#8a2a2a" : "#1a1a19" }}>
                      {stats.dues_overdue}
                    </div>
                    <div className="kv-label" style={{ fontSize: "0.62rem", marginTop: "0.25rem" }}>
                      {t("dashboard.overdueLabel")}
                    </div>
                  </div>
                  <AlertCircle size={26} strokeWidth={1.3} color={stats.dues_overdue > 0 ? "#8a2a2a" : "#858481"} />
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Anniversaries of the month — admins only */}
      {anniv && (
        <div className="mt-10 panel" data-testid="anniversaries-widget">
          <div className="panel-head flex items-center" style={{ justifyContent: "space-between" }}>
            <span className="flex items-center gap-2">
              <Cake size={14} color="#e8c97a" />
              {t("dashboard.anniversaries")} — {monthLabel(`${anniv.year}-${String(anniv.month).padStart(2, "0")}`, i18n.language).toUpperCase()}
            </span>
            <span className="flex items-center gap-1">
              <button type="button" onClick={() => shiftAnnivMonth(-1)} data-testid="anniv-prev-month"
                      style={{ background: "none", border: "1px solid rgba(232,201,122,0.4)", borderRadius: 6, color: "#e8c97a", cursor: "pointer", padding: "1px 9px", fontSize: "0.85rem" }}>‹</button>
              <button type="button" onClick={() => shiftAnnivMonth(1)} data-testid="anniv-next-month"
                      style={{ background: "none", border: "1px solid rgba(232,201,122,0.4)", borderRadius: 6, color: "#e8c97a", cursor: "pointer", padding: "1px 9px", fontSize: "0.85rem" }}>›</button>
            </span>
          </div>
          <div className="panel-body">
            {anniv.items.length === 0 && (
              <p style={{ fontStyle: "italic", color: "#858481" }} data-testid="anniversaries-empty">{t("dashboard.annivEmpty")}</p>
            )}
            <div className="space-y-2">
              {anniv.items.map((it, idx) => (
                <div key={`${it.member_id}-${it.type}`} className="kv-row" data-testid={`anniversary-row-${idx}`}
                     style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{
                    minWidth: 34, textAlign: "center", fontWeight: 800, color: "#7a5e2a",
                    border: "1px solid var(--cream-200)", borderRadius: 8, padding: "3px 0", fontSize: "0.8rem",
                  }}>{it.day}</span>
                  <Link to={`/members/${it.member_id}`} className="kv-value" style={{ flex: 1, textAlign: "left", textDecoration: "none", color: "inherit", fontWeight: 600 }}>
                    {it.name}
                  </Link>
                  <span className="kv-label" style={{ fontSize: "0.72rem" }}>
                    {it.type === "birthday"
                      ? <>🎂 {t("dashboard.annivBirthday")}</>
                      : <>🔷 {it.years === 1 ? t("dashboard.annivInitiationOne") : t("dashboard.annivInitiation", { years: it.years })}</>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Membership Statistics */}
      <div className="mt-10 panel">
        <div className="panel-head">{t("dashboard.membershipStats")}</div>
        <div className="panel-body">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {membership.map((m) => (
              <div key={m.label} className="text-center py-3" style={{ borderRight: "1px dashed var(--cream-200)" }}>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Icon3D name={m.icon3d} size={40} alt={m.label} />
                </div>
                <div className="tile-value" style={{ fontSize: "1.6rem", marginTop: "0.3rem" }}>{m.value}</div>
                <div className="kv-label">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Two columns: Upcoming Events + Last Updates */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
        <section>
          <h2 className="section-heading">{t("dashboard.upcomingEventsTitle")}</h2>
          <div className="gold-divider mt-2 mb-4" />
          {events.length === 0 && <p style={{ fontStyle: "italic", color: "#858481" }}>{t("dashboard.noUpcomingEvents")}</p>}
          <div className="space-y-3">
            {events.map((ev) => (
              <div key={ev.id} className="event-card">
                <div className="event-date">
                  {new Date(ev.date).toLocaleDateString(t("dashboard.en_gb"), { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="event-title">{ev.title}</div>
                {ev.location && <div style={{ color: "#858481", fontStyle: "italic", fontSize: "0.9rem" }}>{ev.location}</div>}
              </div>
            ))}
          </div>
        </section>

        <section data-testid="dashboard-last-updates">
          <h2 className="section-heading">{t("dashboard.lastUpdates")}</h2>
          <div className="gold-divider mt-2 mb-4" />
          {activity.length === 0 && <p style={{ fontStyle: "italic", color: "#858481" }}>{t("dashboard.noRecentUpdates")}</p>}
          <div>
            {activity.map((a) => (
              <div key={a.id} className="activity-item" data-testid={`activity-item-${a.id}`}>
                <div className="activity-dot" />
                <div>
                  <div className="activity-text" data-testid={`activity-text-${a.id}`}>{a.text}</div>
                  <div className="activity-meta">{new Date(a.created_at).toLocaleString(t("dashboard.en_gb"))} · {a.actor}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Ofițeri */}
      {lodge && (
        <div className="mt-10 panel">
          <div className="panel-head">{t("dashboard.officers")}</div>
          <div className="panel-body">
            {OFFICER_FIELDS.map((f) => {
              const val = lodge[f.key] || (f.fallback ? lodge[f.fallback] : "") || "—";
              return (
                <div key={f.key} className="kv-row">
                  <span className="kv-label">{f.label}</span>
                  <span className="kv-value">{val}</span>
                </div>
              );
            })}
            <div style={{ textAlign: "right", marginTop: "0.8rem" }}>
              <Link to="/lodge-info" className="btn-outline-gold" style={{ textDecoration: "none" }}>
                {t(isObedience ? "dashboard.grandLodgeDetails" : "dashboard.lodgeDetails")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
